/**
 * Driver Idle Warning Banner
 * ---------------------------
 * Polls /api/driver-performance/warnings/active every 30s while the
 * driver is signed in. When a warning row exists:
 *   • Renders a loud red banner at the top of the driver screen.
 *   • Plays a short alert sound (once per new warning).
 *   • Forces the driver to acknowledge with a reason before dismissing
 *     (Lunch break / Traffic / Customer reschedule / Other + optional note).
 *
 * Mount this near the root of any driver-only layout. It self-hides
 * when the user has no active warnings.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Short alert chime hosted on the mixkit CDN (same source we already
// use for the delivery celebration sound — no extra licence needed).
const ALERT_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/2870/2870-preview.mp3';

const REASON_OPTIONS = [
  { value: 'lunch_break', label: 'Lunch / Break' },
  { value: 'traffic', label: 'Traffic / Roadblock' },
  { value: 'customer_reschedule', label: 'Customer rescheduled' },
  { value: 'other', label: 'Other' },
];

const DriverIdleWarningBanner = () => {
  const [warnings, setWarnings] = useState([]);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const seenIdsRef = useRef(new Set());
  const audioRef = useRef(null);

  const fetchActive = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(
        `${API_BASE_URL}/driver-performance/warnings/active`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const next = res.data?.warnings || [];
      setWarnings(next);
      // Play alert on a NEW (unseen) warning.
      const newOnes = next.filter((w) => !seenIdsRef.current.has(w.id));
      if (newOnes.length) {
        newOnes.forEach((w) => seenIdsRef.current.add(w.id));
        try {
          if (!audioRef.current) audioRef.current = new Audio(ALERT_SOUND_URL);
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(() => {});
        } catch (_) { /* sound is best-effort */ }
        // Buzz the phone if supported.
        try { navigator.vibrate?.([220, 90, 220]); } catch (_) { /* ignore */ }
      }
    } catch (_) {
      // Silently ignore — banner just won't show.
    }
  };

  useEffect(() => {
    fetchActive();
    const t = setInterval(fetchActive, 30000);
    return () => clearInterval(t);
  }, []);

  if (!warnings.length) return null;

  // We always act on the OLDEST active warning so the queue drains FIFO.
  const current = warnings[warnings.length - 1];

  const titleMap = {
    inter_stop_idle: '⏱ You\'ve been idle',
    warehouse_delay: '🚚 Time to roll',
  };
  const subtitleMap = {
    inter_stop_idle: `You delivered #${current.previous_order_number || ''} a while ago. Mark order #${current.next_order_number || ''} as On The Way to continue.`,
    warehouse_delay: `You picked your orders ${current.picked_count ? `(${current.picked_count}) ` : ''}a while ago. Mark your first delivery as On The Way.`,
  };

  const handleDismiss = async () => {
    if (!reason) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/driver-performance/warnings/${current.id}/dismiss`,
        { reason, note: note || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOpen(false);
      setReason('');
      setNote('');
      // Optimistic remove
      setWarnings((prev) => prev.filter((w) => w.id !== current.id));
    } catch (_) {
      // Allow retry
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="sticky top-0 z-40 bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg animate-pulse"
        data-testid="driver-idle-warning-banner"
      >
        <div className="max-w-screen-md mx-auto px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm">{titleMap[current.type] || 'Heads up'}</p>
            <p className="text-xs text-white/90 leading-snug">{subtitleMap[current.type] || ''}</p>
          </div>
          <Button
            size="sm"
            onClick={() => setOpen(true)}
            className="bg-white text-red-700 hover:bg-white/90 font-semibold flex-shrink-0"
            data-testid="driver-idle-warning-action-btn"
          >
            Acknowledge
          </Button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-slate-900 w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl border border-slate-700">
            <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="font-semibold text-white">Why the delay?</h3>
              <button
                onClick={() => setOpen(false)}
                className="text-gray-400 hover:text-white"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-gray-300">
                Help your team understand what's going on. Pick the closest reason — it just goes to the dispatch log, not your customers.
              </p>

              <div className="grid grid-cols-1 gap-2" data-testid="dismiss-reasons">
                {REASON_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
                      reason === opt.value
                        ? 'border-emerald-500 bg-emerald-500/10'
                        : 'border-slate-700 bg-slate-800 hover:border-slate-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="reason"
                      value={opt.value}
                      checked={reason === opt.value}
                      onChange={(e) => setReason(e.target.value)}
                      className="w-4 h-4 accent-emerald-500"
                      data-testid={`dismiss-reason-${opt.value}`}
                    />
                    <span className="text-white text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>

              <textarea
                placeholder="Anything else? (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 text-sm"
                data-testid="dismiss-note"
              />

              <Button
                onClick={handleDismiss}
                disabled={!reason || submitting}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-4"
                data-testid="dismiss-confirm-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving…
                  </>
                ) : (
                  'Submit & Get Back to Work'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DriverIdleWarningBanner;
